import { useCallback, useEffect, useMemo, useState } from "react";
import { Paper, Box, Button, FormControlLabel, Switch, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Chip, ButtonGroup, IconButton } from "@mui/material";
import { DocumentStatus } from "../documentsApi";
import { AppId } from "../../../app/appTypes";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { DocumentChangeLog, DocumentVersionHistory, loadDocumentFullVersionHistory } from "./documentsChangeLogApi";
import { ErrorContainer } from "../../../app/ErrorContainer";
import { LoadingContainer } from "../../../app/LoadingContainer";
import { unwrapResult } from "@reduxjs/toolkit";
import { DeleteDocumentChangeLog } from "./dialogs/DeleteDocumentChangeLog";
import { History } from "@mui/icons-material"
import LayersIcon from '@mui/icons-material/Layers';
import React from "react";
import { DocumentStatusLabel } from "../DocumentStatusLabel";
import { If } from "../../../app/If";
import { Localized } from "@fluent/react";
import { getLink } from "../components/DocumentLinkProvider";
import { useNavigate } from "react-router-dom";
import { Edit, Delete, Add } from "@mui/icons-material";
import { WarningAmber } from "@mui/icons-material";
import { AddEditDocumentChangeLog } from "./dialogs/AddEditDocumentChangeLog";
import produce from "immer";
import { PrintPreviewChangeLogView } from "./PrintPreviewChangeLogView";
import { isDocEditable } from "../documentSlice";

export interface DocumentHistoryViewProps {
  documentId: AppId
  canEdit: boolean,
  enablePreview?: boolean
}

type DialogState
  = "none"
  | "addEditDocumentChangeLog"
  | "deleteDocumentChangeLog"


export const DocumentHistoryView = ({ documentId, canEdit, enablePreview = false }: DocumentHistoryViewProps) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)
  const [error, setError] = useState(null);
  const [documentVersionHistory, setDocumentVersionHistory] = useState<DocumentVersionHistory[]>([]);
  const [isPreview, setIsPreview] = useState(false);
  const [selectedLogRow, setSelectedLogRow] = useState<DocumentChangeLog | null>(null);
  const [dialog, setDialog] = useState<DialogState>("none")
  const [mainVersion, setMainVersion] = useState<DocumentVersionHistory | null>(null);
  const isEditable = useAppSelector(isDocEditable)

  const allLogs = documentVersionHistory
    .flatMap((v) =>
      v.changeLogs.map((log) => ({
        ...log,
        version: v.version,
        createdBy: v.createdBy,
      }))
    )
    .sort((a, b) => new Date(b.createDate).getTime() - new Date(a.createDate).getTime());


  useEffect(() => {
    setIsLoading(true)
    dispatch(loadDocumentFullVersionHistory({ documentId: documentId }))
      .then(unwrapResult)
      .then((versions: DocumentVersionHistory[]) => {
        setDocumentVersionHistory(versions);

        const main = versions.find(v => v.isMainDocument);
        if (main) {
          setMainVersion(main);
        }
      })
      .catch((error) => { console.error(error); setIsError(true); setError(error); })
      .finally(() => setIsLoading(false))
  }, [])

  // ======================================================
  //  Handlers
  // ======================================================
  const handleGoToClick = (type: string, id: string | number) => {
    const link = getLink(type, id);
    navigate(link);
  };

  const handleEdit = (log: DocumentChangeLog) => {
    setSelectedLogRow(log);
    setDialog("addEditDocumentChangeLog");
  };

  const handleDelete = (log: DocumentChangeLog) => {
    setDialog("deleteDocumentChangeLog")
    setSelectedLogRow(log);
  };


  const handleDialogCancelClick = useCallback(() => {
    setDialog("none")
    setSelectedLogRow(null);
  }, [])


  const onChangeLogSave = (changeLog: DocumentChangeLog) => {
    setDocumentVersionHistory(produce(draft => {
      const mainVersion = draft.find(v => v.isMainDocument);
      if (mainVersion) {
        const index = mainVersion.changeLogs.findIndex(l => l.id === changeLog.id);

        if (index !== -1) {
          mainVersion.changeLogs[index] = changeLog;
        } else {
          mainVersion.changeLogs.push(changeLog);
        }
      }
    }));
    setDialog("none");
  };

  const onChangeLogDeleteSuccess = (logId: AppId) => {
    setDocumentVersionHistory(produce(draft => {
      const mainVersion = draft.find(v => v.isMainDocument);
      if (mainVersion) {
        mainVersion.changeLogs = mainVersion.changeLogs.filter(log => log.id !== logId);
      }
    }));
    setSelectedLogRow(null);
    setDialog("none")
  };

  const versionsSorted = useMemo(
    () => [...documentVersionHistory].sort(
      (a, b) => (b.version ?? -Infinity) - (a.version ?? -Infinity)
    ),
    [documentVersionHistory]
  );

  // ======================================================
  //  Render
  // ======================================================
  if (isError) {
    return <>
      <ErrorContainer error={error} />
    </>
  }

  if (isLoading) {
    return <>
      <LoadingContainer />
    </>
  }


  return (
    <>
      {enablePreview && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
          <FormControlLabel
            control={<Switch checked={isPreview} onChange={() => setIsPreview(v => !v)} />}
            label={<Localized id="print-preview">
              <span>_Print preview</span>
            </Localized>}
          />
        </Box>
      )}
      {enablePreview && isPreview ? (
        <PrintPreviewChangeLogView versions={documentVersionHistory} />
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table >
              <TableHead>
                <TableRow>
                  <TableCell colSpan={6} sx={{ bgcolor: "#f9f9f9", fontWeight: "bold" }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <LayersIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
                      <Localized id="document-versions">
                        <span>_Document versions</span>
                      </Localized>
                    </Box>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <b>
                      <Localized id="document-number">
                        <span>_Number</span>
                      </Localized>
                    </b>
                  </TableCell>
                  <TableCell>
                    <b>
                      <Localized id="document-version">
                        <span>_Version</span>
                      </Localized>
                    </b>
                  </TableCell>
                  <TableCell>
                    <b>
                      <Localized id="document-author">
                        <span>_Author</span>
                      </Localized>
                    </b>
                  </TableCell>
                  <TableCell>
                    <b>
                      <Localized id="document-created-date">
                        <span>_Create date</span>
                      </Localized>
                    </b>
                  </TableCell>
                  <TableCell>
                    <b>
                      <Localized id="document-status">
                        <span>_Status</span>
                      </Localized>
                    </b>
                  </TableCell>
                  <TableCell />
                </TableRow>

              </TableHead>
              <TableBody>
                {versionsSorted.map((doc) => (
                  <React.Fragment key={doc.version}>
                    <TableRow selected={doc.isMainDocument}>
                      <TableCell>{doc.no}</TableCell>
                      <TableCell>
                        {doc.version}
                      </TableCell>
                      <TableCell>{doc.createdByFullName}</TableCell>
                      <TableCell>
                        {new Date(doc.createDate).toISOString().split("T")[0]}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={<DocumentStatusLabel status={doc.status as DocumentStatus} />}
                          size="small"
                          variant="filled"
                          color={doc.isMainDocument ? "info" : "primary"}
                        />

                      </TableCell>
                      <TableCell>
                        <If condition={doc.id !== documentId}
                          otherwise={
                            <Chip
                              label={<Localized id="currently-viewed">
                                <span>_currently viewed</span>
                              </Localized>}
                              color="success"
                              variant="outlined"
                              size="small"
                              sx={{ ml: 1 }}
                            />}
                        >
                          <ButtonGroup size="small">
                            <Button
                              style={{ width: 75 }}
                              color="secondary"
                              variant="outlined"
                              onClick={() => handleGoToClick(doc.type, doc.id)}
                            >
                              <Localized id="go-to">
                                <span>_goto</span>
                              </Localized>
                            </Button>
                          </ButtonGroup>
                        </If>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TableContainer component={Paper} sx={{ mt: 3 }}>
            <Table >
              <TableHead>
                <TableRow>
                  <TableCell colSpan={7} sx={{ bgcolor: "#f9f9f9", fontWeight: "bold" }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <History fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
                      <Localized id="document-changelog-description">
                        <span>_Changelog</span>
                      </Localized>
                    </Box>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <b>
                      <Localized id="lp">
                        <span>_Lp.</span>
                      </Localized>
                    </b>
                  </TableCell>
                  <TableCell>
                    <b>
                      <Localized id="document-version">
                        <span>_Version</span>
                      </Localized>
                    </b>
                  </TableCell>
                  <TableCell>
                    <b>
                      <Localized id="document-changelog-change-description">
                        <span>_Change description</span>
                      </Localized>
                    </b>
                  </TableCell>
                  <TableCell>
                    <b>
                      <Localized id="document-changelog-change-reason">
                        <span>_Change reason</span>
                      </Localized>
                    </b>
                  </TableCell>
                  <TableCell>
                    <b>
                      <Localized id="created-by">
                        <span>_Created by</span>
                      </Localized>
                    </b>
                  </TableCell>
                  <TableCell>
                    <b>
                      <Localized id="date">
                        <span>_Date</span>
                      </Localized>
                    </b>
                  </TableCell>
                  <TableCell>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={{ "& .MuiTableCell-root": { py: 0.5 } }}>
                {allLogs.map((log, index) => (
                  <TableRow hover key={`${log.version}-${log.id}`}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{log.version}</TableCell>
                    <TableCell>{log.changeDescription}</TableCell>
                    <TableCell>{log.changeReason}</TableCell>
                    <TableCell>{log.createdByFullName}</TableCell>
                    <TableCell>{new Date(log.createDate).toLocaleDateString("pl-PL")}</TableCell>
                    <TableCell>
                      <If condition={canEdit}>
                        <>
                          <IconButton
                            onClick={() => handleEdit(log)}
                            sx={{
                              color: "action.active",
                              "&:hover": { color: "info.main" }
                            }}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDelete(log)}
                            sx={{
                              color: "action.active",
                              "&:hover": { color: "error.main" }
                            }}
                          >
                            <Delete />
                          </IconButton>
                        </>
                      </If>
                    </TableCell>


                  </TableRow>
                ))}

                {allLogs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ fontStyle: "italic", color: "gray" }}>
                      <WarningAmber fontSize="small" sx={{ mr: 1, color: "warning.main" }} />

                      <Localized id="document-changelog-no-entries">
                        <span>_No entries in changelog</span>
                      </Localized>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>

            </Table>

            <Button
              variant="contained"
              color="secondary"
              startIcon={<Add />}
              size="small"
              sx={{ m: 1 }}
              disabled={!canEdit}
              onClick={() => setDialog("addEditDocumentChangeLog")}
            >
              <Localized id="document-changelog-add-entry">
                <span>_Add entry to changelog</span>
              </Localized>
            </Button>
          </TableContainer>
          {
            dialog === "addEditDocumentChangeLog" &&
            <AddEditDocumentChangeLog
              documentId={mainVersion?.id ?? ""}
              mode={selectedLogRow ? "edit" : "create"}
              changeLogRow={selectedLogRow}
              onCancelClick={handleDialogCancelClick}
              onSuccessfulSave={onChangeLogSave}
              documentVersion={mainVersion?.version ?? 0}
            />
          }
          {
            dialog === "deleteDocumentChangeLog" && selectedLogRow?.id &&
            <DeleteDocumentChangeLog
              documentId={mainVersion?.id ?? ""}
              logId={selectedLogRow?.id}
              onSuccessfulSave={onChangeLogDeleteSuccess}
              onCancel={handleDialogCancelClick}
            />
          }
        </>
      )}
    </>
  );
};

export default DocumentHistoryView;
